import {addMinutes} from 'date-fns';
import debounce from 'lodash.debounce';
import {getType} from 'mobx-state-tree';
import * as React from 'react';
import {findDOMNode} from 'react-dom';
import Flex from '../../../components/Flex';
import {
  ButtonsTemplateFormat,
  EventFormat,
  EventFormatType,
  GenericTemplateFormat,
  GenericTemplateFormatType,
  ImageFormat,
  ImageFormatType,
  MultipleImageFormat,
  MultipleImageFormatType,
  TextFormat,
  TicketsTemplateFormat,
  TicketsTemplateFormatType,
  TranslationFormat
} from '../../../models/chatChannelModel/message/format';
import {styled} from '../../../utils';
import ChatBubbleGroup from '../ChatBubbleGroup';
import ChatEventMessage from '../ChatEventMessage';
import ChatGenericTemplateMessage from '../ChatGenericTemplateMessage';
import ChatMessage from '../ChatMessage';
import ImageMessage from '../ImageMessage';
import MultipleImageMessage from '../MultipleImageMessage/MultipleImageMessage';
import TicketsMessage from '../TicketsMessage/TicketsMessage';
import {ChatFeedProps} from './types';

const Root = styled('div')({
  height: '100%'
});

const Header = styled('header')({
  width: '100%',
  height: 0
});

const ChatNotification = styled('div')(({theme}) => ({
  fontFamily: theme.fontFamily.base,
  fontSize: 12,
  color: '#fff',
  background: 'rgba(0,0,0,0.8)',
  padding: 9,
  textAlign: 'center',
  position: 'relative',
  top: 10,
  width: 200,
  margin: '0 auto',
  zIndex: 1
}));

const Main = styled('main')({
  overflow: 'auto',
  height: '100%'
});

interface ChatFeedState {
  scrolledBeforeMsgAdded: boolean;
  showsNewMsgNotification: boolean;
}

export default class ChatFeed extends React.Component<
  ChatFeedProps,
  ChatFeedState
> {
  public static defaultProps = {
    currentUserId: 0,
    loadMoreOffsetHeight: 10,
    groupCutOffAfter: 20
  };

  private _mainNode: HTMLElement;
  private _prevScrollHeight: number;
  private _prevScrollTop: number;

  constructor(props: ChatFeedProps) {
    super(props);
    this.state = {
      scrolledBeforeMsgAdded: false,
      showsNewMsgNotification: false
    };
  }

  public componentDidMount() {
    if (typeof this.props.loadMore === 'function' && this._mainNode) {
      const mainNode = findDOMNode(this._mainNode);
      (mainNode as HTMLElement).addEventListener(
        'scroll',
        debounce(this._handleScroll, 100)
      );
    }

    this._scrollToBottom();
  }

  public componentWillReceiveProps(nextProps: ChatFeedProps) {
    if (nextProps.messages.length > this.props.messages.length) {
      this.setState({scrolledBeforeMsgAdded: this._scrolled});
      this._prevScrollHeight = this._mainNode.scrollHeight;
      this._prevScrollTop = this._mainNode.scrollTop;
    }
  }

  public componentDidUpdate(prevProps: ChatFeedProps) {
    const {messages, currentUserId} = this.props;

    const previousLastMessage =
      prevProps.messages[prevProps.messages.length - 1];

    const lastMessage = messages[messages.length - 1];
    const messagesAdded = messages.length > prevProps.messages.length;

    const messagesAppended =
      messages.indexOf(previousLastMessage) > -1 &&
      messagesAdded &&
      JSON.stringify(lastMessage) !== JSON.stringify(previousLastMessage);

    const messagesAppendedByCurrentUser =
      messagesAppended && lastMessage.senderId === currentUserId;
    const messagesAppendedByOtherParticipants =
      messagesAppended && !messagesAppendedByCurrentUser;

    const messagesPrepended = messagesAdded && !messagesAppended;

    if (messagesAppendedByCurrentUser) {
      this._scrollToBottom();
    }

    if (messagesAppendedByOtherParticipants) {
      if (this.state.scrolledBeforeMsgAdded) {
        this.setState({showsNewMsgNotification: true});
      } else {
        this._scrollToBottom();
      }
    }

    if (messagesPrepended) {
      this._mainNode.scrollTop =
        this._mainNode.scrollHeight -
        this._prevScrollHeight +
        this._prevScrollTop;
    }
  }

  public render() {
    return (
      <Root>
        <Header>
          {this.state.showsNewMsgNotification
            ? this._renderNewMessageNotification()
            : null}
        </Header>
        <Main className={this.props.className} innerRef={this._setRootNode}>
          {this._renderHistory()}
        </Main>
      </Root>
    );
  }

  private _renderHistory() {
    const {
      messages,
      currentUserId,
      showSenderName: _showSenderName,
      groupCutOffAfter
    } = this.props;

    let groupedMessages: ChatMessage[] = [];

    const history = messages.map((message, index) => {
      const {format} = message;
      const formatType = getType(format);

      if (formatType === EventFormat) {
        return (
          <ChatEventMessage
            message={message as ChatMessage<EventFormatType>}
            key={index}
          />
        );
      }

      if (formatType === GenericTemplateFormat) {
        return (
          <ChatGenericTemplateMessage
            message={message as ChatMessage<GenericTemplateFormatType>}
            key={index}
          />
        );
      }

      if (formatType === ImageFormat) {
        return (
          <ImageMessage
            message={message as ChatMessage<ImageFormatType>}
            key={index}
          />
        );
      }

      if (formatType === MultipleImageFormat) {
        return (
          <MultipleImageMessage
            message={message as ChatMessage<MultipleImageFormatType>}
            key={index}
          />
        );
      }

      if (formatType === ButtonsTemplateFormat) {
        return null;
      }

      if (formatType === TicketsTemplateFormat) {
        return (
          <TicketsMessage
            message={message as ChatMessage<TicketsTemplateFormatType>}
            key={index}
          />
        );
      }

      groupedMessages.push(message);

      const nextMessage = messages[index + 1];
      const nextMessageIsText =
        nextMessage &&
        (getType(nextMessage.format) === TextFormat ||
          getType(nextMessage.format) === TranslationFormat);
      const timeToCutOff = addMinutes(message.createdAt, groupCutOffAfter!);

      if (
        !nextMessage ||
        !nextMessageIsText ||
        nextMessage.senderId !== message.senderId ||
        timeToCutOff < new Date(nextMessage.createdAt)
      ) {
        const showSenderName = _showSenderName && message.senderId !== 0;
        const currentGroupMessages = groupedMessages;
        groupedMessages = [];

        return (
          <ChatBubbleGroup
            messages={currentGroupMessages}
            fromCurrentUser={currentUserId === message.senderId}
            showSenderName={showSenderName!}
            showStartTime={true}
            key={index}
          />
        );
      }

      return null;
    });

    return <Flex direction="column">{history}</Flex>;
  }

  private _renderNewMessageNotification() {
    return <ChatNotification>You have new messages.</ChatNotification>;
  }

  private _setRootNode = (node: HTMLElement) => {
    this._mainNode = node;
  };

  private _scrollToBottom() {
    if (this._mainNode) {
      this._mainNode.scrollTop =
        this._maxScrollTop > 0 ? this._maxScrollTop : 0;
    }
  }

  private _handleScroll = () => {
    const {loadMore, loadMoreOffsetHeight} = this.props;

    if (
      this._mainNode.scrollTop < loadMoreOffsetHeight! &&
      typeof loadMore === 'function'
    ) {
      loadMore();
    }

    if (this._mainNode.scrollTop === this._maxScrollTop) {
      this.setState({showsNewMsgNotification: false});
    }
  };

  private get _maxScrollTop() {
    const {scrollHeight, clientHeight} = this._mainNode;
    return scrollHeight - clientHeight;
  }

  private get _scrolled() {
    return this._mainNode.scrollTop < this._maxScrollTop;
  }
}



// WEBPACK FOOTER //
// ./src/components/ChatUI/ChatFeed/ChatFeed.tsx